import { defineStore } from 'pinia'
import { ref, computed, onUnmounted, onMounted } from 'vue'
import type { Vehicle, CreateVehicleRequest, UpdateVehicleRequest } from '../types/vehicle'
import type { SearchFilters } from '../types/search'
import { vehicleService } from '../services/VehicleService'

import { conflictResolutionService } from '../services/ConflictResolutionService'

export const useVehicleStore = defineStore('vehicle', () => {
  // State
  const vehicles = ref<Vehicle[]>([])
  const selectedVehicle = ref<Vehicle | null>(null)
  const isLoading = ref(false)
  const error = ref<string | null>(null)
  const searchFilters = ref<SearchFilters>({})

  let conflictResolutionListener: ((event: CustomEvent) => void) | null = null
  let entityRefreshListener: ((event: CustomEvent) => void) | null = null

  // Getters
  const availableVehicles = computed(() => 
    vehicles.value.filter(vehicle => vehicle.status === 'available')
  )

  const inUseVehicles = computed(() => 
    vehicles.value.filter(vehicle => vehicle.status === 'in_use')
  )

  const vehicleCount = computed(() => vehicles.value.length)

  const availableVehicleCount = computed(() => availableVehicles.value.length)

  const filteredVehicles = computed(() => {
    if (!searchFilters.value.vehicleRegistration && !searchFilters.value.vehicleId) {
      return vehicles.value
    }

    return vehicles.value.filter(vehicle => {
      const matchesRegistration = !searchFilters.value.vehicleRegistration || 
        vehicle.registrationNumber.toLowerCase().includes(searchFilters.value.vehicleRegistration.toLowerCase())
      
      const matchesId = !searchFilters.value.vehicleId || 
        vehicle.id === searchFilters.value.vehicleId

      return matchesRegistration && matchesId
    })
  })

  // Actions
  const setLoading = (loading: boolean) => {
    isLoading.value = loading
  }

  const setError = (errorMessage: string | null) => {
    error.value = errorMessage
  }

  const clearError = () => {
    error.value = null
  }

  const setVehicles = (vehicleList: Vehicle[]) => {
    vehicles.value = vehicleList
  }

  const addVehicle = (vehicle: Vehicle) => {
    vehicles.value.unshift(vehicle) // Add to beginning for newest first
  }

  const updateVehicleInList = (updatedVehicle: Vehicle) => {
    const index = vehicles.value.findIndex(v => v.id === updatedVehicle.id)
    if (index !== -1) {
      vehicles.value[index] = updatedVehicle
    }
  }

  const removeVehicleFromList = (vehicleId: string) => {
    const index = vehicles.value.findIndex(v => v.id === vehicleId)
    if (index !== -1) {
      vehicles.value.splice(index, 1)
    }
    // Clear selected vehicle if it was deleted
    if (selectedVehicle.value?.id === vehicleId) {
      selectedVehicle.value = null
    }
  }

  const setSelectedVehicle = (vehicle: Vehicle | null) => {
    selectedVehicle.value = vehicle
  }

  const setSearchFilters = (filters: SearchFilters) => {
    searchFilters.value = { ...filters }
  }

  const clearSearchFilters = () => {
    searchFilters.value = {}
  }

  // Service integration actions
  const fetchAllVehicles = async (): Promise<void> => {
    try {
      setLoading(true)
      clearError()
      const vehicleList = await vehicleService.getAllVehicles()
      setVehicles(vehicleList)
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch vehicles'
      setError(errorMessage)
      throw err
    } finally {
      setLoading(false)
    }
  }

  const fetchVehicleById = async (id: string): Promise<Vehicle | null> => {
    try {
      setLoading(true)
      clearError()
      const vehicle = await vehicleService.getVehicleById(id)
      return vehicle
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch vehicle'
      setError(errorMessage)
      throw err
    } finally {
      setLoading(false)
    }
  }

  const createVehicle = async (vehicleData: CreateVehicleRequest): Promise<Vehicle> => {
    try {
      setLoading(true)
      clearError()
      const newVehicle = await vehicleService.createVehicle(vehicleData)
      addVehicle(newVehicle)
      return newVehicle
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to create vehicle'
      setError(errorMessage)
      throw err
    } finally {
      setLoading(false)
    }
  }

  const updateVehicle = async (id: string, vehicleData: UpdateVehicleRequest): Promise<Vehicle> => {
    try {
      setLoading(true)
      clearError()
      
      // Check for optimistic lock before updating
      const currentVehicle = vehicles.value.find(v => v.id === id)
      if (currentVehicle) {
        const lockData = conflictResolutionService.getOptimisticLock(id)
        if (lockData && !conflictResolutionService.checkOptimisticLock(id, lockData.version)) {
          // Fetch latest version to detect conflicts
          const latestVehicle = await vehicleService.getVehicleById(id)
          if (latestVehicle) {
            const conflictedFields = conflictResolutionService.getConflictedFieldsForVehicle(currentVehicle, latestVehicle)
            if (conflictedFields.length > 0) {
              conflictResolutionService.detectConflict(id, 'vehicle', currentVehicle, latestVehicle, conflictedFields)
              throw new Error('Conflict detected - please resolve before updating')
            }
          }
        }
      }
      
      const updatedVehicle = await vehicleService.updateVehicle(id, vehicleData)
      updateVehicleInList(updatedVehicle)
      
      // Update optimistic lock
      conflictResolutionService.setOptimisticLock(id, Date.now(), updatedVehicle.updatedAt)
      
      // Update selected vehicle if it's the one being updated
      if (selectedVehicle.value?.id === id) {
        setSelectedVehicle(updatedVehicle)
      }
      
      return updatedVehicle
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to update vehicle'
      setError(errorMessage)
      throw err
    } finally {
      setLoading(false)
    }
  }

  const deleteVehicle = async (id: string): Promise<void> => {
    try {
      setLoading(true)
      clearError()
      await vehicleService.deleteVehicle(id)
      removeVehicleFromList(id)
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to delete vehicle'
      setError(errorMessage)
      throw err
    } finally {
      setLoading(false)
    }
  }

  const searchVehicles = async (filters: SearchFilters): Promise<Vehicle[]> => {
    try {
      setLoading(true)
      clearError()
      const results = await vehicleService.searchVehicles(filters)
      setSearchFilters(filters)
      return results
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to search vehicles'
      setError(errorMessage)
      throw err
    } finally {
      setLoading(false)
    }
  }

  const fetchVehiclesByStatus = async (status: 'available' | 'in_use'): Promise<Vehicle[]> => {
    try {
      setLoading(true)
      clearError()
      const results = await vehicleService.getVehiclesByStatus(status)
      return results
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch vehicles by status'
      setError(errorMessage)
      throw err
    } finally {
      setLoading(false)
    }
  }

  const checkRegistrationUniqueness = async (registrationNumber: string, excludeId?: string): Promise<boolean> => {
    try {
      return await vehicleService.isRegistrationNumberUnique(registrationNumber, excludeId)
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to check registration uniqueness'
      setError(errorMessage)
      throw err
    }
  }



  const transformVehicleFromDatabase = (dbVehicle: any): Vehicle => {
    return {
      id: dbVehicle.id,
      registrationNumber: dbVehicle.registration_number,
      vehicleType: dbVehicle.vehicle_type,
      brandModel: dbVehicle.brand_model,
      seatingCapacity: dbVehicle.seating_capacity,
      status: dbVehicle.status,
      createdAt: new Date(dbVehicle.created_at),
      updatedAt: new Date(dbVehicle.updated_at)
    }
  }

  const handleConflictResolution = async (resolution: any) => {
    try {
      if (resolution.resolution === 'local') {
        // Keep local version - no action needed
        return
      } else if (resolution.resolution === 'remote') {
        // Use remote version - refresh from server
        const conflictData = conflictResolutionService.getConflict(resolution.conflictId)
        if (conflictData) {
          updateVehicleInList(conflictData.remoteVersion)
        }
      } else if (resolution.resolution === 'merge' && resolution.mergedData) {
        // Apply merged data
        const vehicleId = resolution.mergedData.id
        if (vehicleId) {
          await updateVehicle(vehicleId, resolution.mergedData)
        }
      }
    } catch (error) {
      console.error('Error handling conflict resolution:', error)
    }
  }

  const refreshVehicle = async (vehicleId: string) => {
    try {
      const refreshedVehicle = await vehicleService.getVehicleById(vehicleId)
      if (refreshedVehicle) {
        updateVehicleInList(refreshedVehicle)
        if (selectedVehicle.value?.id === vehicleId) {
          setSelectedVehicle(refreshedVehicle)
        }
      }
    } catch (error) {
      console.error('Error refreshing vehicle:', error)
    }
  }

  // Setup conflict resolution listeners
  onMounted(() => {
    // Listen for conflict resolutions
    conflictResolutionListener = (event: CustomEvent) => {
      const resolution = event.detail
      if (resolution.conflictId.includes('vehicle')) {
        handleConflictResolution(resolution)
      }
    }
    window.addEventListener('conflict-resolved', conflictResolutionListener as EventListener)

    // Listen for entity refresh requests
    entityRefreshListener = (event: CustomEvent) => {
      const { entityType, entityId } = event.detail
      if (entityType === 'vehicle') {
        refreshVehicle(entityId)
      }
    }
    window.addEventListener('entity-refresh-requested', entityRefreshListener as EventListener)
  })

  // Cleanup on store destruction
  onUnmounted(() => {
    if (conflictResolutionListener) {
      window.removeEventListener('conflict-resolved', conflictResolutionListener as EventListener)
    }
    if (entityRefreshListener) {
      window.removeEventListener('entity-refresh-requested', entityRefreshListener as EventListener)
    }
  })

  // Reset store state
  const resetStore = () => {
    vehicles.value = []
    selectedVehicle.value = null
    isLoading.value = false
    error.value = null
    searchFilters.value = {}

  }

  return {
    // State
    vehicles,
    selectedVehicle,
    isLoading,
    error,
    searchFilters,

    
    // Getters
    availableVehicles,
    inUseVehicles,
    vehicleCount,
    availableVehicleCount,
    filteredVehicles,
    
    // Actions
    setLoading,
    setError,
    clearError,
    setVehicles,
    addVehicle,
    updateVehicleInList,
    removeVehicleFromList,
    setSelectedVehicle,
    setSearchFilters,
    clearSearchFilters,
    
    // Service integration
    fetchAllVehicles,
    fetchVehicleById,
    createVehicle,
    updateVehicle,
    deleteVehicle,
    searchVehicles,
    fetchVehiclesByStatus,
    checkRegistrationUniqueness,
    

    
    // Utilities
    resetStore
  }
})